import React, { useEffect, useState } from 'react';
import axiosInstance from '../../api/commonAPI';

interface StoreOwner {
    id: number;
    username: string;
    role: string;
    approved: boolean;
    storeId: number | null;
    storeName: string | null;
    storeAddress: string | null;
    createdAt: string | null;
    updatedAt: string | null;
}

interface Invitation {
    id: number;
    code: string;
    used: boolean;
    expiresAt: string;
    usedAt: string | null;
    createdBy: string;
}

const StoreOwnerManagementPage: React.FC = () => {
    const [storeOwners, setStoreOwners] = useState<StoreOwner[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [invitation, setInvitation] = useState<Invitation | null>(null);

    const fetchStoreOwners = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const { data } = await axiosInstance.get<StoreOwner[]>('/api/admins/store-owners');
            setStoreOwners(data);
        } catch {
            setError('상점주인 목록을 불러오지 못했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchStoreOwners();
    }, []);

    const handleCreateInvitation = async () => {
        setError(null);

        try {
            const { data } = await axiosInstance.post<Invitation>('/api/admin/auth/invitations');
            setInvitation(data);
        } catch {
            setError('인증코드 생성에 실패했습니다.');
        }
    };

    const handleApprovalChange = async (id: number, approved: boolean) => {
        setError(null);

        try {
            const { data } = await axiosInstance.patch<StoreOwner>(
                `/api/admins/store-owners/${id}/approval`,
                null,
                { params: { approved } }
            );
            setStoreOwners(prev => prev.map(owner => owner.id === id ? data : owner));
        } catch {
            setError('상점주인 상태 변경에 실패했습니다.');
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('이 상점주인 계정을 삭제할까요?')) {
            return;
        }

        setError(null);

        try {
            await axiosInstance.delete(`/api/admins/store-owners/${id}`);
            setStoreOwners(prev => prev.filter(owner => owner.id !== id));
        } catch {
            setError('상점주인 삭제에 실패했습니다.');
        }
    };

    return (
        <main style={containerStyle}>
            <div style={headerStyle}>
                <div>
                    <h1 style={titleStyle}>상점주인 관리</h1>
                    <p style={descriptionStyle}>상점주인 계정 상태를 관리하고 가입용 인증코드를 발급합니다.</p>
                </div>
                <button type="button" onClick={handleCreateInvitation} style={primaryButtonStyle}>
                    인증코드 발급
                </button>
            </div>

            {invitation && (
                <section style={inviteBoxStyle}>
                    <span style={labelStyle}>새 인증코드</span>
                    <strong style={codeStyle}>{invitation.code}</strong>
                    <span style={mutedStyle}>만료일: {formatDate(invitation.expiresAt)}</span>
                </section>
            )}

            {error && <p style={errorStyle}>{error}</p>}

            <section style={tableWrapStyle}>
                {isLoading ? (
                    <p style={emptyStyle}>불러오는 중...</p>
                ) : storeOwners.length === 0 ? (
                    <p style={emptyStyle}>등록된 상점주인이 없습니다.</p>
                ) : (
                    <table style={tableStyle}>
                        <thead>
                            <tr>
                                <th style={thStyle}>아이디</th>
                                <th style={thStyle}>매장</th>
                                <th style={thStyle}>상태</th>
                                <th style={thStyle}>가입일</th>
                                <th style={thStyle}>관리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {storeOwners.map(owner => (
                                <tr key={owner.id}>
                                    <td style={tdStyle}>{owner.username}</td>
                                    <td style={tdStyle}>
                                        <div>{owner.storeName || '-'}</div>
                                        <small style={mutedStyle}>ID: {owner.storeId ?? '-'}</small>
                                    </td>
                                    <td style={tdStyle}>
                                        <span style={owner.approved ? activeBadgeStyle : inactiveBadgeStyle}>
                                            {owner.approved ? '활성' : '비활성'}
                                        </span>
                                    </td>
                                    <td style={tdStyle}>{formatDate(owner.createdAt)}</td>
                                    <td style={tdStyle}>
                                        <div style={actionsStyle}>
                                            <button
                                                type="button"
                                                onClick={() => handleApprovalChange(owner.id, !owner.approved)}
                                                style={secondaryButtonStyle}
                                            >
                                                {owner.approved ? '비활성화' : '활성화'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(owner.id)}
                                                style={dangerButtonStyle}
                                            >
                                                삭제
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </main>
    );
};

const formatDate = (value: string | null) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('ko-KR');
};

const containerStyle: React.CSSProperties = {
    padding: '32px',
    fontFamily: 'sans-serif'
};

const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    alignItems: 'center',
    marginBottom: '24px'
};

const titleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: '28px'
};

const descriptionStyle: React.CSSProperties = {
    margin: '8px 0 0',
    color: '#666'
};

const tableWrapStyle: React.CSSProperties = {
    border: '1px solid #ddd',
    borderRadius: '8px',
    overflow: 'hidden'
};

const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse'
};

const thStyle: React.CSSProperties = {
    padding: '12px',
    textAlign: 'left',
    backgroundColor: '#f6f7f9',
    borderBottom: '1px solid #ddd'
};

const tdStyle: React.CSSProperties = {
    padding: '12px',
    borderBottom: '1px solid #eee',
    verticalAlign: 'middle'
};

const primaryButtonStyle: React.CSSProperties = {
    padding: '10px 16px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#2563eb',
    color: 'white',
    cursor: 'pointer'
};

const secondaryButtonStyle: React.CSSProperties = {
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    backgroundColor: 'white',
    cursor: 'pointer'
};

const dangerButtonStyle: React.CSSProperties = {
    ...secondaryButtonStyle,
    borderColor: '#dc3545',
    color: '#dc3545'
};

const actionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px'
};

const activeBadgeStyle: React.CSSProperties = {
    padding: '4px 8px',
    borderRadius: '999px',
    backgroundColor: '#e8f7ee',
    color: '#137333',
    fontSize: '13px'
};

const inactiveBadgeStyle: React.CSSProperties = {
    ...activeBadgeStyle,
    backgroundColor: '#fff4e5',
    color: '#a15c00'
};

const inviteBoxStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px',
    border: '1px solid #c7d2fe',
    borderRadius: '8px',
    backgroundColor: '#eef2ff',
    marginBottom: '16px'
};

const labelStyle: React.CSSProperties = {
    color: '#444'
};

const codeStyle: React.CSSProperties = {
    fontSize: '20px',
    letterSpacing: '1px'
};

const mutedStyle: React.CSSProperties = {
    color: '#777'
};

const errorStyle: React.CSSProperties = {
    color: '#dc3545'
};

const emptyStyle: React.CSSProperties = {
    padding: '24px',
    margin: 0,
    color: '#666'
};

export default StoreOwnerManagementPage;
